import React from 'react';
import { Star, ThumbsUp, Flag } from 'lucide-react';

interface Review {
  id: string;
  rating: number;
  comment: string;
  created_at: string;
  user: {
    full_name: string;
    email: string;
  };
}

interface ReviewListProps {
  reviews: Review[];
  isLoading?: boolean;
  error?: string;
}

export default function ReviewList({ reviews, isLoading, error }: ReviewListProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-gray-800/50 rounded-xl p-4 border border-white/5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-700 rounded w-1/4 mb-2"></div>
                  <div className="h-3 bg-gray-700 rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!reviews || reviews.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">Henüz yorum yapılmamış.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => (
        <div 
          key={review.id}
          className="bg-gray-900/50 rounded-xl p-4 border border-white/5"
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-white">
                  {review.user?.full_name || 'Anonim'}
                </span>
                <div className="flex items-center gap-1 bg-yellow-400/10 px-2 py-0.5 rounded-full">
                  <Star className="w-3 h-3 text-yellow-400 fill-current" />
                  <span className="text-xs font-medium text-yellow-400">
                    {review.rating}
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-400">
                {new Date(review.created_at).toLocaleDateString('tr-TR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors">
                <ThumbsUp className="w-4 h-4" />
              </button>
              <button className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors">
                <Flag className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Comment */}
          <p className="text-gray-300">{review.comment}</p>
        </div>
      ))}
    </div>
  );
}